import type { TurtleState } from './types';
import { civilFromEpochDay, dayOfYear, epochDayFromCivil, spanIn } from './calendar';
import { held } from './held';
import { YEAR_DAYS, dailyDeath, type PopulationDraws } from './population';

/**
 * Maui Nui's green sea turtles, the honu (M93): a share of the archipelago's
 * one population, resident foragers that leave to breed (`GDD.md` section 7).
 * Counted by year class since hatching, in four places: the young at sea
 * bound for Maui Nui, the honu at home at Maui Nui, and the adult females and
 * males away breeding at French Frigate Shoals. The day's step ages them on
 * the first of January, then they die, the young recruit from the open ocean,
 * the breeders leave and come home in their months, and the season's
 * hatchlings reach the sea. Its draws are the caller's, so the same step runs
 * the game (binomial and Poisson draws on counts, on the honu's own stream of
 * the day, `stockStream`) and the spin-up that sets its balance (expected
 * values, `scripts/turtles.ts`).
 *
 * **A life** (`TurtleLife`), measured in Hawaii: growth by carapace length
 * from stranded turtles' bones, a turtle's size setting its stage (at sea,
 * young, adult); how often each sex breeds, their eggs and the hatchlings
 * that reach the sea, measured at French Frigate Shoals, where 96% of the
 * population nests (Seminoff et al. 2015), and the adults' sexes from
 * turtles stranded throughout the islands.
 *
 * **Its deaths**: each stage's survival a year, the adults' measured at
 * French Frigate Shoals, the young's at Molokaʻi, and the young at sea's set
 * by the balance: how many of the season's hatchlings live
 * to recruit, no source served here measures (M93), so it is the one fitted
 * number, set so that the archipelago's honu replace themselves and Maui
 * Nui's hold its share of today's adults year on year.
 *
 * **Maui Nui's share** of the archipelago's honu (`MAUI_NUI_SHARE`): no
 * source read counts Maui Nui's honu (M93's readers), so its share is the
 * towed divers' turtles a tow segment at each island times the island's
 * coastline, Maui Nui's four islands over the main islands'; every honu
 * counted as foraging in the main islands (the northwestern atolls' are not
 * counted: open). The young at sea bound for Maui Nui are that share of every
 * hatchling in the archipelago, Maui Nui's breeders' and the rest's; the rest
 * of the archipelago's breeders are held at their share of the nesting count
 * until the region's honu are coded.
 */

/** A sea turtle's life, each figure with its source. */
export interface TurtleLife {
  id: 'honu' | 'honuea';
  /** The growth's start: its carapace's straight length, cm, at an age in years. */
  growthFromCm: number;
  growthFromYears: number;
  /** Growth a year, cm, in each 10 cm class of straight carapace length from `growthFromCm`; none past the last. */
  growthCmPerYear: readonly number[];
  /** The years each cm of growth takes less than the mean rates give (the measured bias of mean rates). */
  biasYearsPerCm: number;
  /**
   * Von Bertalanffy's curve, straight carapace length cm at age t years = L∞ (1 − e^(−k (t − t0))), where a turtle's
   * growth is measured so (the hawksbill's, M187); the classes' growth above is then unused.
   */
  vonBertalanffy?: { linfCm: number; k: number; t0Years: number };
  /** The size it leaves the open ocean for the shallows at, cm. */
  recruitCm: number;
  /** The size it is mature (an adult, breeding) from, cm. */
  matureCm: number;
  /** Survival a year: the young's (from recruiting) and the adults' (at home and away alike). */
  youngSurvival: number;
  adultSurvival: number;
  /** The oldest class, years: the last holds every turtle of that age or older. */
  oldestYears: number;
  /** The adults' share female. */
  femaleShare: number;
  /** Each sex's share of its adults breeding in a year: one over its mean years between breeding. */
  femaleBreeding: number;
  maleBreeding: number;
  /** A breeding female's clutches a season, eggs a clutch, and the share of eggs whose hatchlings reach the sea. */
  clutches: number;
  eggsPerClutch: number;
  emergence: number;
  /** The archipelago's breeding females: its adult females, each breeding once in `1 / femaleBreeding` years. */
  breedingFemales: number;
  /** The breeding calendar, each a span of [month, day] to [month, day], both days in it. */
  leaveHome: Span;
  malesHome: Span;
  femalesHome: Span;
  hatchlingsToSea: Span;
}

/** Days of the year, [month, day] to [month, day], both in it. */
export interface Span {
  from: readonly [number, number];
  to: readonly [number, number];
}

/**
 * The green sea turtle, *Chelonia mydas* (honu), of the Central North
 * Pacific population, each figure with its source as read (M93).
 */
export const HONU: TurtleLife = {
  id: 'honu',
  /**
   * "The mean age of a 30 cm SCL juvenile Hawaiian green sea turtle was
   * estimated to be 5 yr by Zug et al. (2002)" (Murakawa and Snover 2018,
   * p. 190), the start of its growth by size.
   */
  growthFromCm: 30,
  growthFromYears: 5,
  /**
   * Each 10 cm class's mean growth a year, 30-39.9 to 90-99.9 cm, the
   * expectation of the lognormal (Murakawa and Snover 2018, Table 3, "All
   * data combined", 347 growth rates "from the 96 turtles in the present
   * study", stranded turtles' humeri read
   * skeletochronology), "the least biased" of their methods (ibid., abstract):
   * 32.5 years from 30 to 90 cm (95% CI 29.2-41.1).
   */
  growthCmPerYear: [2.49, 2.05, 2.05, 1.68, 1.66, 1.47, 0.53],
  /**
   * "We found an error rate of −0.17 yr cm−1 for mean growth rates calculated
   * using the expectation of the mean. In other words, age is overestimated by
   * 0.17 yr for every 1 cm of growth" (ibid., p. 190), from the turtles whose
   * growth their bones record year by year: mean rates miss the rare growth
   * spurts. Applied to every cm, as they apply it, it gives their "mean age at
   * first reproduction for turtles that experience growth spurts to be 27.3
   * yr (95% CI: 24.0−35.9 yr)" (ibid.); the 37.5 years of the mean rates alone
   * "we consider this estimate to be high".
   */
  biasYearsPerCm: 0.17,
  /**
   * "The smallest juveniles (35–37 cm SCL) in coastal waters were 6 to 9 years
   * old by the CF method and 4 to 10 years old by the SI method" (Zug et al.
   * 2002, pp. 122-123; "the smallest Hawaiian specimen was 34.8 cm SCL", p.
   * 123); "Hawaiian juveniles exit the pelagic phase between the ages of 4 to
   * 10 years" (p. 125). By the growth above, 35 cm is 6.16 years old.
   */
  recruitCm: 35,
  /**
   * The mean size at first breeding, 89.7 cm (82-97) (Van Houtan et al. 2014,
   * as Balazs et al. 2015, Table 2, gives it, and Piacenza et al. 2016,
   * Table 2, CC BY; the size Murakawa and Snover 2018 reckon first breeding
   * from, p. 190): 27.2 years by the growth above.
   * Every honu mature from it, a knife edge (open: the spread of ages at first
   * breeding, 2-9 years between nestings aside).
   */
  matureCm: 89.7,
  /**
   * At Molokaʻi, from "capture-mark–recapture histories for 1,904 uniquely
   * tagged immature turtles sampled since 1982": "the annual apparent
   * survival probability lower for FP-diseased immature turtles (0.78, 95%
   * CI: 0.68–0.85) than for disease-free immatures (0.88, 95% CI:
   * 0.81–0.93), irrespective of size class" (Chaloupka, in Hargrove et al.
   * 2016, p. 57): the disease-free's, as the game's honu are until the
   * disease, fibropapillomatosis, is coded (open). Apparent survival counts a
   * turtle that leaves for good as dead. The southern Great Barrier Reef's,
   * 0.88 for juveniles and 0.85 for subadults (Chaloupka and Limpus 2005, as
   * Seminoff et al. 2015, p. 295, compiles them), are near it.
   */
  youngSurvival: 0.88,
  /**
   * "The top-ranked model predicted constant female survival over time (S =
   * 0.929 yr−1, 95% CI: 0.924−0.933)" (Piacenza et al. 2016, p. 103), from
   * 3,405 females tagged nesting at East Island, its models run "for 1981 to
   * 2009": the adult
   * females'. The males' is unmeasured; taken as the females' (open).
   */
  adultSurvival: 0.929,
  /** "ages ... 3.3 to 49.4 yr (n=103)" (Zug et al. 2002, p. 120), the oldest aged; a nester seen over 38 years "would have been at least 62 years old" (Nurzia Humburg and Balazs 2014, p. 8). The classes run to 50, the last every honu of 50 or older. */
  oldestYears: 50,
  /** "61.6% (172) of the necropsied turtles this size or greater [> 81 cm SCL] were females, and 38.4% (107) were males" (Balazs et al. 2015, p. 125), of "strandings occurring throughout the Hawaiian Islands during 1982 to May 2014". */
  femaleShare: 0.616,
  /** "A mean remigration interval of 4 yrs was calculated during the study period 1973–2012 ... 2–9 yrs" (Balazs et al. 2015, p. 123); the same 4 years in "0.25 (= 4−1 remigration interval)" (Piacenza et al. 2016, Table 2, CC BY) and "nesters return to breed at an interval of 4 years" (Seminoff et al. 2015, p. 339). */
  femaleBreeding: 1 / 4,
  /**
   * "The reproductive cycles of males at French Frigate Shoals have been
   * documented in 16 cases. Nine (56.2%) of these represented a 1-yr cycle,
   * five (31.3%) a 2-yr cycle, and two (12.5%) a 3-yr cycle" (Balazs 1980, p.
   * 15): a mean of 25/16 years, so 16/25 of the males a year. "Greater
   * difficulties are encountered in reliably determining male reproductive
   * cycles that are longer than 1 yr" (ibid.), so it may be too many (open).
   */
  maleBreeding: 16 / 25,
  /**
   * "From 1986 to 1991, complete season nesting surveys were conducted on East
   * Island, resulting in a complete census of the nesting population. The mean
   * clutch frequency during those 5 yrs was 4 clutches per female" (Balazs et
   * al. 2015, p. 122); 1.8 in 1974-75 (Balazs 1980) and 2.8 in 1999 (Pepi
   * 2002) were counted by less complete watches. The same 4, "a clutch
   * frequency of 4 nests" (Seminoff et al. 2015, p. 339).
   */
  clutches: 4,
  /** Clutch size 92.4 eggs (33-150) and "Natural hatchling emergence was 71.1 %", Tern Island, 1986 to 1991, 428 nests (Niethammer et al. 1997, pp. 36 and 40; Balazs et al. 2015, Table 1); the same "clutch size of 92.4 eggs" in Seminoff et al. 2015, p. 339, and its emergence "at 71 percent" (Balazs 1980's). */
  eggsPerClutch: 92.4,
  emergence: 0.711,
  /** "In sum, we estimate the DPS has a total of 3,846 breeding females" (Seminoff et al. 2015, pp. 336-337): the females counted nesting a year, 2009 to 2012, times the remigration interval, 4. */
  breedingFemales: 3846,
  /**
   * "The turtles arrive at the breeding atoll during mid-April to early June"
   * (Dizon and Balazs 1982, p. 14), read as 15 April to 10 June, less the
   * journey: 26 days, the mean of the three turtles tracked home from French
   * Frigate Shoals (23, 26 and 30 days; Balazs and Ellis 2000, pp. 282-283).
   * The day they leave their foraging grounds no source read measures.
   */
  leaveHome: { from: [3, 20], to: [5, 15] },
  /** The two males tracked from French Frigate Shoals arrived home on 8 July (Molokaʻi) and 19 July (Maui) 1995 (Balazs and Ellis 2000, p. 282). */
  malesHome: { from: [7, 8], to: [7, 19] },
  /**
   * The females leave as their nesting ends: its peak ends on 1 August
   * (Niethammer et al. 1997, p. 38: "Oviposition peaked between 15 June and 1
   * August"), home a journey (26 days) later; the one tracked left on 26
   * September and arrived home on 19 October 1995 (Balazs and Ellis 2000, pp.
   * 282-283). So 27 August to 19 October.
   */
  femalesHome: { from: [8, 27], to: [10, 19] },
  /** "Emergence activity peaked between 15 August and 1 October, with 55.6%" of the emergences (Niethammer et al. 1997, p. 39): every hatchling put in it. */
  hatchlingsToSea: { from: [8, 15], to: [10, 1] },
};

/**
 * The hawksbill turtle, *Eretmochelys imbricata* (honuʻea), of the main Hawaiian Islands, each figure with its source as
 * read (M187): a population of its own, nesting on the main islands' beaches, Hawaiʻi Island's above all ("Hawai'i
 * Island (86.4%), Maui (8.1%), Moloka'i (5.5%), and Kaua'i (0.1%)" of 1,280 nests, Gaos et al. 2021, *Front. Mar. Sci.*
 * 8: 770424, p. 5, CC BY), and foraging on their reefs ("most hawksbills in Hawaiʻi spend their entire lives around the
 * archipelago", NOAA Fisheries, 20 July 2020). Its adults away breeding are at their nesting beaches. No survival of a
 * Hawaiian hawksbill at any stage was read (M187's reader): the Great Barrier Reef's, as NOAA's review compiles them.
 */
export const HONUEA: TurtleLife = {
  id: 'honuea',
  /** Unused: its growth is von Bertalanffy's (below). */
  growthFromCm: 0,
  growthFromYears: 0,
  growthCmPerYear: [],
  biasYearsPerCm: 0,
  /**
   * "For Hawaii hawksbills, t0 = −0.17 (62.5 days) and L∞ = 81.0 cm, both from empirical observations. L∞ is the median
   * SCL of 110 neophyte nesters from Hawaii and Maui"; k fitted to its scutes' bomb-radiocarbon ages: "Under the mean
   * growth scenario, turtles matured at 28.6 years (range 23–36 years) with k = 0.128" (Van Houtan, Andrews, Jones,
   * Murakawa and Hagemann 2016, *Proc. R. Soc. B* 283: 20152220, pp. 4 and 5; its facts cited). ESTIMATED, the fit's.
   * Skeletochronology's 17 to 22 years (Snover et al. 2013, as NOAA's 2013 review compiles it, p. 12) fitted its L∞, 95
   * cm, where "measured nesting Hawaii hawksbills are always less than 90 cm" (Van Houtan et al., p. 6).
   */
  vonBertalanffy: { linfCm: 81.0, k: 0.128, t0Years: -0.17 },
  /**
   * The young leave the open ocean at 35 cm: "We find hawksbills 0–4 years of age, measuring 8–34 cm straight carapace
   * length, are found predominantly in the coastal pelagic waters of Hawaii" and "juveniles (35.0–74.4 cm, n = 52) ...
   * are nearshore" (Van Houtan, Francke et al. 2016, *Ecol. Evol.* 6: 2378, pp. 2378 and 2380, CC BY). By the growth
   * above, 35 cm is 4.25 years old.
   */
  recruitCm: 35,
  /** Mature at 97.5% of L∞, 78.975 cm, as Van Houtan et al. take it ("we assume Lm = 0.975*L∞", p. 4): 28.6 years by the growth. A knife edge, as the honu's. */
  matureCm: 0.975 * 81.0,
  /**
   * The Great Barrier Reef's foraging hawksbills, "adult females (0.92), adult males (0.72), juvenile females (0.93),
   * and juvenile males (0.78)" (Bell et al. 2012, as NMFS and USFWS 2013, *Hawksbill Sea Turtle 5-Year Review*, p. 13,
   * compiles them; a US government work), each stage's sexes weighted by Hawaiʻi's share female (below): another
   * ocean's, a foraging ground's as Maui Nui's is (open: none measured in Hawaiʻi). Nesting females at Varanus Island,
   * Western Australia, 0.947 (ibid.).
   */
  youngSurvival: 0.828 * 0.93 + (1 - 0.828) * 0.78,
  adultSurvival: 0.828 * 0.92 + (1 - 0.828) * 0.72,
  /** Its oldest age unread: the classes run to 50, as the honu's, the last every hawksbill of 50 or older (a definition, not a death). */
  oldestYears: 50,
  /** "We were able to determine the sex of 58 turtles, 48 (82.8%) of which were females" (Brunson et al. 2022, *Endang. Species Res.* 47: 109, p. 112, CC BY), juveniles and adults stranded in Hawaiʻi, 1984 to 2018: its turtles' share female. */
  femaleShare: 0.828,
  /** "The mean remigration interval for hawksbills seen across years (n = 178) was 4.3 (±2.7) years, but the modal remigration interval was 3 years" (Gaos et al. 2021, p. 5): the mean, missed returns lengthening it (p. 10; open). */
  femaleBreeding: 1 / 4.3,
  /**
   * Every adult male a year (ESTIMATED): "there were 12 breeding males for 13 breeding females" at one Hawaiʻi Island
   * rookery in 2017, by their hatchlings' parentage (Horne et al. 2023, *R. Soc. Open Sci.* 10: 221547, p. 11, CC BY),
   * near parity, where the adults' share female above leaves fewer males than a near-equal number breeding each year
   * would need of any males breeding less often.
   */
  maleBreeding: 1,
  /** "clutch size (n = 1,083) was 168.9 eggs ± 37.1 (range 34–299 eggs), and clutch frequency (n = 290) was 2.7 ± 1.3 clutches" (Gaos et al. 2021, p. 5). */
  clutches: 2.7,
  eggsPerClutch: 168.9,
  /** "The average hatching success rate (n = 1,064) across all sites and years was 72.5%" (Gaos et al. 2021, p. 5), of the nests excavated; the hatchlings that reach the sea of them unmeasured (open). */
  emergence: 0.725,
  /**
   * The archipelago's adult females: the nesting females a year, Hawaiʻi Island's by its counts corrected for its
   * watchers' effort, "a mean of 27.1 (±13.3) nesting females ... on Hawai'i Island each year" (Gaos et al. 2021, p. 5,
   * ESTIMATED, 1989 to 2018), over its share of the nests ("Hawai'i Island (86.4%), Maui (8.1%), Moloka'i (5.5%), and
   * Kaua'i (0.1%)", p. 5), the shares Maui Nui's is taken from (below), times their mean years between nestings: 134.9.
   * Its share of the tagged nesters, 93.8%, leaves Molokaʻi's out, the nesters tagged on two islands alone ("derived
   * solely from these two islands", p. 3; M187's reviewer). The counts alone, "an annual average of 14 ± 4.3", "likely
   * underestimates" (pp. 1, 6), would give 70; NOAA compiles "less than 100 adult females" (Horne et al., p. 2).
   */
  breedingFemales: (27.1 / 0.864) * 4.3,
  /**
   * ESTIMATED from its nesting season, no arrival or departure of a Hawaiian hawksbill read: "Nesting activity occurred
   * primarily between the months of April and December each year, with peak nesting occurring during the months of
   * July (33.5%) and August (30.1%)" (Gaos et al. 2021, p. 5); the breeders leave over the 45 days from 1 June to
   * mid-July, the peak's first half.
   */
  leaveHome: { from: [6, 1], to: [7, 15] },
  /** The males home as the peak begins, their mating done before the females nest (ESTIMATED; none tracked). */
  malesHome: { from: [7, 15], to: [8, 15] },
  /**
   * The females home after their season's clutches, "2.7 ± 1.3 clutches", "a mean internesting interval for hawksbills
   * (n = 517) was 23.3 (±9.9) days" (Gaos et al. 2021, p. 5): about 40 days after the first, the peak's nesters home from
   * mid-August to mid-October (ESTIMATED).
   */
  femalesHome: { from: [8, 15], to: [10, 15] },
  /** The peak's nests' hatchlings, "The average incubation period (n = 561) across all sites and years was 61.3 ± 7.4 days" (Gaos et al. 2021, p. 5): July's and August's nests reaching the sea in September and October. */
  hatchlingsToSea: { from: [9, 1], to: [10, 31] },
};

/**
 * Green turtles a thousand five-minute tow segments of the towed divers'
 * track, by island: "Table 8. Density of green turtles in the Hawaiian
 * Archipelago and Johnston Atoll ... Table modified from the supplemental
 * materials provided by Becker et al. (2019). Density represents the number
 * of green turtles per 1,000 tow segments or kilometer" (NMFS 2023, p. 154),
 * NOAA's surveys along the ~15 m contour ("the HIIS surveys, unlike other
 * regions, ended in 2010", Becker et al. 2019, p. 7). The table's turtles a
 * kilometer are not taken: Oʻahu's, 0.11 against its 74 a thousand segments,
 * makes its segments 673 m long, where every other island's make them 222 to
 * 239 m (its turtles a kilometer over its turtles a segment; review M93), so
 * a segment, a five-minute tow, is near enough the same length everywhere
 * but where Oʻahu's kilometers went wrong. Kahoʻolawe's coast was not towed.
 */
export const TOWED_TURTLES_PER_1000_SEGMENTS = { hawaii: 60, maui: 54, kauai: 43, molokai: 30, oahu: 74, lanai: 23, niihau: 7 } as const;
/**
 * Each main island's general coastline, km: "Table 5.05-- GENERAL COASTLINE
 * AND TIDAL SHORELINE BY COUNTY AND ISLAND", the general coastline in
 * kilometers ("Figures are lengths of general outline of seacoast"; "Derived
 * from data expressed in statute miles ... 1 mi. = 1.609 km"), from "U.S.
 * Department of Commerce, National Ocean Survey, The Coastline of the United
 * States (1975) and records" (The State of Hawaii Data Book 2024, Table
 * 5.05; the 2023 edition's the same). Its six other islands' are the figures
 * Feirstein and Fletcher 2004 print, whose Kauaʻi's ("220 km of
 * diversified", Kauaʻi County's 137 miles) and Niʻihau's (145 km, Kauaʻi's)
 * were wrong (M100). "Data for the four islands of Maui County are not
 * consistent with the reported county total" (ibid.): the four islands' add
 * to 458 km, the county's 338. The islands' are taken: no curve around an
 * island is shorter than its convex hull, and the four's hulls on GEBCO
 * 2026's land are 191.3, 138.6, 74.4 and 47.4 km, 451.7 together (MEASURED,
 * M100, `scripts/coastHulls.ts`), each within 2.4% of its island's figure
 * (Maui's 0.9%, Molokaʻi's 2.4%, Lānaʻi's 2.1%, Kahoʻolawe's 0.9%; review M116:
 * "within 2%" was two of them over) and
 * together longer than the county's.
 */
export const COASTLINE_KM = { hawaii: 428, kahoolawe: 47, kauai: 145, lanai: 76, maui: 193, molokai: 142, niihau: 72, oahu: 180 } as const;

/**
 * Maui Nui's islands' honu, each island's turtles a tow segment times its
 * coastline. Kahoʻolawe, not towed, at the other three's turtles a segment,
 * weighted by coast.
 */
export function mauiNuiWeights(perSegment: typeof TOWED_TURTLES_PER_1000_SEGMENTS, coastKm: typeof COASTLINE_KM): Record<'maui' | 'molokai' | 'lanai' | 'kahoolawe', number> {
  const [maui, molokai, lanai] = [perSegment.maui * coastKm.maui, perSegment.molokai * coastKm.molokai, perSegment.lanai * coastKm.lanai];
  return { maui, molokai, lanai, kahoolawe: ((maui + molokai + lanai) / (coastKm.maui + coastKm.molokai + coastKm.lanai)) * coastKm.kahoolawe };
}

/** Maui Nui's share of the archipelago's honu: its islands' weights (`mauiNuiWeights`) over the main islands'. */
export function mauiNuiShare(perSegment: typeof TOWED_TURTLES_PER_1000_SEGMENTS, coastKm: typeof COASTLINE_KM): number {
  const w = mauiNuiWeights(perSegment, coastKm);
  const mauiNui = w.maui + w.molokai + w.lanai + w.kahoolawe;
  const rest = perSegment.hawaii * coastKm.hawaii + perSegment.oahu * coastKm.oahu + perSegment.kauai * coastKm.kauai + perSegment.niihau * coastKm.niihau;
  return mauiNui / (mauiNui + rest);
}
export const MAUI_NUI_SHARE = mauiNuiShare(TOWED_TURTLES_PER_1000_SEGMENTS, COASTLINE_KM);

/**
 * Maui Nui's share of the archipelago's hawksbills (M187): its share of the nests, "Maui (8.1%), Moloka'i (5.5%)" of
 * the 1,280 recorded (Gaos et al. 2021, p. 5): ESTIMATED, each turtle taken to forage near where it nests, none counted
 * foraging (they "disperse across hundreds of km to various foraging areas throughout the archipelago", Horne et al.
 * 2023, p. 2, compiled; west Maui among their "Primary foraging habitats", NOAA's PIRO brochure, p. 2, unsourced).
 */
export const HONUEA_MAUI_NUI_SHARE = 0.081 + 0.055;

/** A turtle's share of the archipelago at Maui Nui: the honu's by the towed divers, the hawksbill's by its nests. */
export const mauiNuiShareOf = (life: TurtleLife): number => (life.id === 'honu' ? MAUI_NUI_SHARE : HONUEA_MAUI_NUI_SHARE);

/** The archipelago's adults: its breeding females over the adults' share female. */
export const archipelagoAdults = (life: TurtleLife): number => life.breedingFemales / life.femaleShare;

/** Hatchlings reaching the sea from one breeding female's season. */
export const hatchlingsPerFemale = (life: TurtleLife): number => life.clutches * life.eggsPerClutch * life.emergence;

/** What the balance sets and what the rest of the archipelago sends (`data/turtles/`). */
export interface TurtleSupply {
  /** The share of the young at sea alive a year on: fitted (`scripts/turtles.ts`). */
  seaSurvival: number;
  /** The share of the archipelago's young bound for Maui Nui (`MAUI_NUI_SHARE`). */
  share: number;
  /** The rest of the archipelago's breeding females a season, held: its share of the breeding females, a quarter of them a year. */
  restBreeders: number;
}

/** A stock on the balance's first of January (`data/turtles/`): none away and no hatchlings due on that day. */
export interface TurtleJanuary {
  atSea: readonly number[];
  home: readonly number[];
}

/** The young recruit evenly through the year of their age centered on `recruitCm`'s, half a year each side (a definition: the age's spread is unmeasured, Zug's 4 to 10 years the span of the smallest inshore turtles' ages). */
const RECRUIT_HALF_YEARS = 0.5;

/** The classes: 0 to `oldestYears`, the last every turtle of that age or older. */
export const turtleClasses = (life: TurtleLife): number => life.oldestYears + 1;

/** Years each cm takes in a 10 cm class: its mean rate's, less the bias. */
const yearsPerCm = (life: TurtleLife, bin: number): number => 1 / (life.growthCmPerYear[bin] as number) - life.biasYearsPerCm;

/** Straight carapace length, cm, at an age in years: the growth's start before it, the last class's end (no growth measured past it) after. */
export function carapaceCm(life: TurtleLife, ageYears: number): number {
  const vb = life.vonBertalanffy;
  if (vb) return Math.max(0, vb.linfCm * (1 - Math.exp(-vb.k * (ageYears - vb.t0Years))));
  let t = ageYears - life.growthFromYears;
  if (t <= 0) return life.growthFromCm;
  let cm = life.growthFromCm;
  for (let bin = 0; bin < life.growthCmPerYear.length; bin++) {
    const per = yearsPerCm(life, bin);
    if (t < 10 * per) return cm + t / per;
    t -= 10 * per;
    cm += 10;
  }
  return cm;
}

/** The age in years a turtle reaches a length, cm, by its growth. */
export function ageAtCm(life: TurtleLife, cm: number): number {
  const vb = life.vonBertalanffy;
  if (vb) return vb.t0Years - Math.log(1 - cm / vb.linfCm) / vb.k;
  let age = life.growthFromYears;
  for (let bin = 0, from = life.growthFromCm; from < cm; bin++, from += 10) age += Math.min(10, cm - from) * yearsPerCm(life, bin);
  return age;
}

/** How far into a span a day of a year is (0 its first day), or −1 outside it; and the span's days. */
function inSpan(span: Span, epochDay: number, year: number): { day: number; days: number } {
  const { first, days } = spanIn(span, year);
  const day = epochDay - first;
  return { day: day >= 0 && day < days ? day : -1, days };
}

/** The day of a common year the season's hatchlings reach the sea on, on average: the middle of their span (2001's, a common year), a life's worked out once. */
const hatchDays = new Map<TurtleLife, number>();
function hatchDay(life: TurtleLife): number {
  let day = hatchDays.get(life);
  if (day === undefined) {
    const { first, days } = spanIn(life.hatchlingsToSea, 2001);
    day = dayOfYear(first) + (days - 1) / 2;
    hatchDays.set(life, day);
  }
  return day;
}

/** A class's turtles' mean age in years on a day of the year, from the season's mean day of reaching the sea: class 0 this year's hatchlings. */
const ageOn = (life: TurtleLife, cls: number, doy: number): number => cls + (doy - hatchDay(life)) / YEAR_DAYS;
export const classAgeYears = (life: TurtleLife, cls: number, epochDay: number): number => ageOn(life, cls, dayOfYear(epochDay));

export type TurtleStage = 'young' | 'adult';
const stageOf = (life: TurtleLife, ageYears: number): TurtleStage => (carapaceCm(life, ageYears) >= life.matureCm ? 'adult' : 'young');

/** Each class's stage at home on each day of the year (1 to 366), a life's: worked out once, as the fish's classes are. */
const stagesByLife = new Map<TurtleLife, readonly (readonly TurtleStage[])[]>();
export function stagesOn(life: TurtleLife, epochDay: number): readonly TurtleStage[] {
  let byDay = stagesByLife.get(life);
  if (!byDay) {
    // A day's classes' ages are their day of the year's (`classAgeYears`), whatever the year.
    const first = epochDayFromCivil(2000, 1, 1);
    byDay = Array.from({ length: 367 }, (_, doy) => Array.from({ length: turtleClasses(life) }, (_, c) => stageOf(life, classAgeYears(life, c, first + Math.max(doy, 1) - 1))));
    stagesByLife.set(life, byDay);
  }
  return byDay[dayOfYear(epochDay)] as readonly TurtleStage[];
}

/** On the first of January, every class a year older, the oldest gathering every turtle past its age; on any other day, nothing. */
function ageTurtles(life: TurtleLife, t: TurtleState, epochDay: number): void {
  if (dayOfYear(epochDay) !== 1) return;
  const oldest = turtleClasses(life) - 1;
  for (const counts of [t.atSea, t.home, t.awayFemales, t.awayMales]) {
    counts[oldest] = (counts[oldest] as number) + (counts[oldest - 1] as number);
    for (let c = oldest - 1; c > 0; c--) counts[c] = counts[c - 1] as number;
    counts[0] = 0;
  }
}

/**
 * A day of the honu's lives, the classes aged (`ageTurtles`):
 *
 * 1. **Deaths**, each class's at its stage's rate: at sea by the balance's,
 *    at home by its size, away an adult's.
 * 2. **Recruits**: the young at sea leave the open ocean for Maui Nui
 *    evenly through the year of their age around `recruitCm`'s, each day the
 *    share of those left that the rest of the year leaves to it.
 * 3. **Breeders leave** home over `leaveHome`: each day the share of the adults
 *    at home that brings, by the span's end, `femaleBreeding` of the females
 *    and `maleBreeding` of the males (the adults' sexes at `femaleShare`),
 *    split by sex as the breeders are; each female leaving sends her
 *    season's hatchlings, Maui Nui's share of them bound for it, as do the
 *    rest's breeders, a day's share of them over the span.
 * 4. **Breeders come home**: the males over `malesHome`, the females over
 *    `femalesHome`, each day the share of those away the rest of the span
 *    leaves to it, the last day all.
 * 5. **Hatchlings reach the sea** over `hatchlingsToSea` the same way.
 */
export function stepHonu(life: TurtleLife, t: TurtleState, epochDay: number, supply: TurtleSupply, draws: PopulationDraws): void {
  ageTurtles(life, t, epochDay);
  liveHonu(life, t, epochDay, supply, draws);
}

function liveHonu(life: TurtleLife, t: TurtleState, epochDay: number, supply: TurtleSupply, draws: PopulationDraws): void {
  const classes = turtleClasses(life);
  const sea = dailyDeath(supply.seaSurvival);
  const adult = dailyDeath(life.adultSurvival);
  const young = dailyDeath(life.youngSurvival);
  const recruitAge = ageAtCm(life, life.recruitCm);
  const stages = stagesOn(life, epochDay);
  const doy = dayOfYear(epochDay);
  const year = civilFromEpochDay(epochDay).year;
  const die = (counts: number[], c: number, p: number): void => {
    const n = counts[c] as number;
    if (n > 0) counts[c] = n - draws.share(n, p);
  };
  // 1. Deaths.
  for (let c = 0; c < classes; c++) {
    die(t.atSea, c, sea);
    die(t.home, c, stages[c] === 'adult' ? adult : young);
    die(t.awayFemales, c, adult);
    die(t.awayMales, c, adult);
  }
  // 2. Recruits: through the year of age from half a year before `recruitAge` to half a year after.
  for (let c = 0; c < classes; c++) {
    const n = t.atSea[c] as number;
    const age = ageOn(life, c, doy);
    if (n <= 0 || age < recruitAge - RECRUIT_HALF_YEARS || age >= recruitAge + RECRUIT_HALF_YEARS) continue;
    const moved = draws.share(n, Math.min(1, 1 / YEAR_DAYS / (recruitAge + RECRUIT_HALF_YEARS - age)));
    t.atSea[c] = n - moved;
    t.home[c] = (t.home[c] as number) + moved;
  }
  // 3. Breeders leave.
  const leave = inSpan(life.leaveHome, epochDay, year);
  if (leave.day >= 0) {
    const females = life.femaleShare * life.femaleBreeding;
    const breeding = females + (1 - life.femaleShare) * life.maleBreeding;
    const p = breeding / (leave.days - breeding * leave.day);
    const hatchlings = supply.share * hatchlingsPerFemale(life);
    for (let c = 0; c < classes; c++) {
      const n = t.home[c] as number;
      if (n <= 0 || stages[c] !== 'adult') continue;
      const leaving = draws.share(n, p);
      const f = draws.share(leaving, females / breeding);
      t.home[c] = n - leaving;
      t.awayFemales[c] = (t.awayFemales[c] as number) + f;
      t.awayMales[c] = (t.awayMales[c] as number) + leaving - f;
      t.hatchlings += hatchlings * f;
    }
    t.hatchlings += (hatchlings * supply.restBreeders) / leave.days;
  }
  // 4. Breeders come home.
  for (const [away, span] of [[t.awayMales, life.malesHome], [t.awayFemales, life.femalesHome]] as const) {
    const back = inSpan(span, epochDay, year);
    if (back.day < 0) continue;
    for (let c = 0; c < classes; c++) {
      const n = away[c] as number;
      if (n <= 0) continue;
      const home = draws.share(n, 1 / (back.days - back.day));
      away[c] = n - home;
      t.home[c] = (t.home[c] as number) + home;
    }
  }
  // 5. Hatchlings reach the sea.
  const sea0 = inSpan(life.hatchlingsToSea, epochDay, year);
  if (sea0.day >= 0) {
    const released = t.hatchlings / (sea0.days - sea0.day);
    t.hatchlings -= released;
    t.atSea[0] = (t.atSea[0] as number) + draws.count(released);
  }
  // The hatchlings due reach the save: held, as every stored float is (review M76).
  t.hatchlings = held(t.hatchlings);
}

/** One tagged honu (M148): its year class, its sex, and whether it is away breeding at French Frigate Shoals. */
export interface OneHonu { cls: number; female: boolean; away: boolean }

/**
 * One tagged honu's day, as its class's at home or away (`liveHonu`'s rates): a class older on the first of January,
 * then dead by its stage's chance at home (an adult's away), or not; an adult at home leaving over `leaveHome` by its
 * sex's share of breeders spread over the span's days left (`femaleBreeding` or `maleBreeding` in all, as the
 * population's), one away coming home over its sex's span as the population's do. `u` its own even draws. True if it died.
 */
export function stepOneHonu(life: TurtleLife, h: OneHonu, epochDay: number, u: () => number): boolean {
  if (dayOfYear(epochDay) === 1) h.cls = Math.min(h.cls + 1, turtleClasses(life) - 1);
  const stage = stagesOn(life, epochDay)[h.cls];
  if (u() < dailyDeath(h.away || stage === 'adult' ? life.adultSurvival : life.youngSurvival)) return true;
  const year = civilFromEpochDay(epochDay).year;
  if (!h.away && stage === 'adult') {
    const leave = inSpan(life.leaveHome, epochDay, year);
    const b = h.female ? life.femaleBreeding : life.maleBreeding;
    if (leave.day >= 0 && u() < b / (leave.days - b * leave.day)) h.away = true;
  } else if (h.away) {
    const back = inSpan(h.female ? life.femalesHome : life.malesHome, epochDay, year);
    if (back.day >= 0 && u() < 1 / (back.days - back.day)) h.away = false;
  }
  return false;
}

export function emptyTurtles(life: TurtleLife): TurtleState {
  const none = (): number[] => Array<number>(turtleClasses(life)).fill(0);
  return { atSea: none(), home: none(), awayFemales: none(), awayMales: none(), hatchlings: 0 };
}

/** What the card reads on a day: the honu at Maui Nui, young and adults, and the adults away breeding, females and males. */
export interface TurtleCounts {
  young: number;
  adults: number;
  awayFemales: number;
  awayMales: number;
}
export function turtleCounts(life: TurtleLife, t: TurtleState, epochDay: number): TurtleCounts {
  const out: TurtleCounts = { young: 0, adults: 0, awayFemales: 0, awayMales: 0 };
  const stages = stagesOn(life, epochDay);
  for (let c = 0; c < turtleClasses(life); c++) {
    const n = t.home[c] as number;
    if (stages[c] === 'adult') out.adults += n;
    else out.young += n;
    out.awayFemales += t.awayFemales[c] as number;
    out.awayMales += t.awayMales[c] as number;
  }
  return out;
}
